import moment from "moment";
import sessionHandling from "services/utils/notificationUtils";

// Professional Tax APIs (PT Frontend API Guide, base
// /api/payroll/statutory/pt/). ptRequest is shared by every PT screen
// (Configuration, Statutory › PT and Reports). The Configuration screens
// keep the ptConfigData.js form shape ("180" not "180.00", "" for empty,
// months as strings, exemptions as `exemptions`, one components list); the
// mappers here convert to and from the API shape.

const BASE = "payroll/statutory/pt/";

// 409s the PT API sends on purpose; any other 409 is the session check.
const PT_CONFLICT_CODES = ["VERSION_LOCKED", "STALE_VERSION", "RUN_FINALISED", "ALREADY_REVERSED", "NOT_REVERSIBLE"];

export class PtApiError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message);
    this.name = "PtApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

const errorText = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(errorText).join(" ");
  if (value && typeof value === "object") return Object.values(value).map(errorText).join(" ");
  return "";
};

// { "slabs.2.amount": ["…"] } -> { "slabs.2.amount": "…" }
const firstMessages = (error) =>
  error && typeof error === "object" && !Array.isArray(error)
    ? Object.fromEntries(
        Object.entries(error)
          .filter(([key]) => key !== "code")
          .map(([key, messages]) => [key, errorText(Array.isArray(messages) ? messages[0] : messages)]),
      )
    : undefined;

// The guide's optional Idempotency-Key header is not sent (same as ESI and
// LWF: the server's CORS settings block requests that carry it). Save
// buttons show a loading state instead, so a double click can't send twice.
// A FormData body goes as multipart; responseType "blob" returns
// { blob, fileName } for downloads.
export const ptRequest = async ({ domain, path, method = "GET", query, body, signal, responseType }) => {
  const search = query ? new URLSearchParams(Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")) : null;
  const url = `${domain.replace(/\/$/, "")}/${BASE}${path}${search && String(search) ? `?${search}` : ""}`;
  const multipart = typeof FormData !== "undefined" && body instanceof FormData;
  const response = await fetch(url, {
    method,
    signal,
    headers: {
      ...(multipart ? {} : { "Content-Type": "application/json" }),
      Authorization: `Token ${localStorage.getItem("token")}`,
    },
    ...(body ? { body: multipart ? body : JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new PtApiError("Your session has expired. Please sign in again.", { status: 401 });
  }
  if (responseType === "blob" && response.ok) {
    const disposition = response.headers?.get?.("Content-Disposition") || "";
    const fileName = /filename="?([^";]+)"?/i.exec(disposition)?.[1] || "";
    return { blob: await response.blob(), fileName };
  }
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (response.status === 409 && !PT_CONFLICT_CODES.includes(data?.code)) sessionHandling();
  if (!response.ok) {
    throw new PtApiError(errorText(data?.error || data?.detail || data) || `Request failed (${response.status}). Please try again.`, {
      status: response.status,
      code: data?.code || data?.error?.code,
      fieldErrors: firstMessages(data?.error),
    });
  }
  return data;
};

const notFoundToNull = (err) => {
  if (err.status === 404) return null;
  throw err;
};

// ---- Value helpers ----

const text = (value) => (value == null ? "" : String(value));
// "180.00" -> "180"; "0.50" -> "0.5".
export const trimNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const out = String(value);
  return out.includes(".") ? out.replace(/0+$/, "").replace(/\.$/, "") : out;
};
const apiValue = (value) => (value === "" || value == null ? null : String(value).trim());
const apiInt = (value) => (value === "" || value == null || !Number.isFinite(Number(value)) ? null : Number(value));
const upper = (value) => String(value || "").toUpperCase();
const STATUS_WORD = { ACTIVE: "Active", INACTIVE: "Inactive" };
const statusWord = (value) => STATUS_WORD[upper(value)] || text(value) || "Active";
const VERSION_STATUS_WORD = { DRAFT: "Draft", SAVED: "Active", ACTIVE: "Active", SCHEDULED: "Scheduled", SUPERSEDED: "Superseded" };
const apiId = (value) => (/^\d+$/.test(String(value)) ? Number(value) : value);
const months = (list) => (Array.isArray(list) ? list.map(Number).filter(Boolean).sort((a, b) => a - b).map(String) : []);

// A list item that may be a string or an object with a name / label.
export const nameOf = (item) =>
  item == null ? "" : typeof item === "object" ? text(item.name ?? item.label ?? item.code ?? item.key ?? item.value) : text(item);

// ---- Slabs and exemption reasons ----

let rowSeq = 5000;
const nextRowId = () => {
  rowSeq += 1;
  return rowSeq;
};

const fromApiSlab = (slab) => ({
  id: nextRowId(),
  wage_from: trimNumber(slab.wage_from),
  wage_to: trimNumber(slab.wage_to),
  amount: trimNumber(slab.amount),
  applies_to: upper(slab.applies_to) || "ALL",
  special_month_amount: trimNumber(slab.special_month_amount),
});

const GROUP_ORDER = { ALL: 0, WOMEN: 1, MEN: 2 };
const sortedSlabs = (slabs) =>
  [...slabs].sort((a, b) => (GROUP_ORDER[a.applies_to] ?? 9) - (GROUP_ORDER[b.applies_to] ?? 9) || Number(a.wage_from || 0) - Number(b.wage_from || 0));

const toApiSlabs = (slabs, specialOn) =>
  sortedSlabs(slabs).map((slab, index) => ({
    seq: index + 1,
    applies_to: slab.applies_to || "ALL",
    wage_from: apiValue(slab.wage_from),
    wage_to: apiValue(slab.wage_to),
    amount: apiValue(slab.amount),
    special_month_amount: specialOn ? apiValue(slab.special_month_amount) : null,
  }));

const fromApiExemption = (item) => ({
  id: nextRowId(),
  server_id: item.id ?? null,
  reason: text(item.reason),
  mode: upper(item.mode) || "HR_MARKS",
  age_from: item.age_from == null ? "" : String(item.age_from),
  rule_text: text(item.rule_text),
  proof_required: Boolean(item.proof_required),
  status: statusWord(item.status),
});

const toApiExemptions = (list) =>
  list.map((item, index) => ({
    seq: index + 1,
    reason: text(item.reason).trim(),
    mode: item.mode,
    age_from: item.mode === "AUTO_AGE" ? apiInt(item.age_from) : null,
    rule_text: item.mode === "AUTO_AGE" ? "" : text(item.rule_text).trim(),
    proof_required: Boolean(item.proof_required),
    status: upper(item.status) || "ACTIVE",
  }));

// ---- Versions ----

// PT_PERIOD_WAGE (the period total) and PT (the slab amount) are fixed:
// they open read-only.
const FIXED_CODES = ["PT_PERIOD_WAGE", "PT"];
const bySequence = (list) => [...(list || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

const fromApiComponent = (component, kind) => ({
  code: text(component.code),
  name: text(component.name),
  kind,
  description: text(component.description),
  formula: text(component.formula),
  used_for: text(component.used_for),
  status: statusWord(component.status),
  ...(kind === "CONTRIBUTION" || component.fixed || FIXED_CODES.includes(component.code) ? { fixed: true } : {}),
});

export const fromApiPtVersion = (api) => ({
  id: api.id ?? null,
  name: text(api.name),
  description: text(api.description),
  effective_from: text(api.effective_from),
  effective_to: text(api.effective_to),
  status: api.display_status || VERSION_STATUS_WORD[upper(api.status)] || "Draft",
  state: text(api.state),
  local_body: text(api.local_body),
  registration_no: text(api.registration_no),
  frequency: text(api.frequency),
  period_start_months: months(api.period_start_months),
  collect_mode: text(api.collect_mode),
  payment_due_days: text(api.payment_due_days),
  settle_leavers: api.settle_leavers !== false,
  special_month_on: api.special_month != null && api.special_month !== "",
  special_month: api.special_month == null ? "" : String(api.special_month),
  yearly_limit: trimNumber(api.yearly_limit),
  rounding: text(api.rounding) || "NEAREST",
  slabs: (api.slabs || []).filter((slab) => slab && typeof slab === "object").map(fromApiSlab),
  exemptions: (api.exemption_reasons || []).filter((item) => item && typeof item === "object").map(fromApiExemption),
  components: [
    ...bySequence(api.calculation_components).map((component) => fromApiComponent(component, "CALCULATION")),
    ...bySequence(api.contribution_components).map((component) => fromApiComponent(component, "CONTRIBUTION")),
  ],
  copied_from: api.copied_from_site ? nameOf(api.copied_from_site) : "",
  source_version_id: api.source_version_id ?? null,
  is_locked: Boolean(api.is_locked),
  used_by: (api.used_by || []).map(nameOf).filter(Boolean),
  row_version: api.row_version ?? null,
  created_by: text(api.saved_by ?? api.created_by),
  created_at: text(api.saved_at ?? api.created_at),
});

// The version body for POST versions/, PUT versions/<id>/, PUT draft/ and
// test-employee (the unsaved version). State and local body come from the
// site and are not sent.
export const toApiPtVersion = (form, companyId) => {
  const kindIndex = { CALCULATION: 0, CONTRIBUTION: 0 };
  const monthly = form.frequency === "MONTHLY";
  return {
    company_id: Number(companyId),
    name: text(form.name).trim(),
    description: text(form.description),
    effective_from: apiValue(form.effective_from),
    registration_no: text(form.registration_no).trim(),
    frequency: apiValue(form.frequency),
    period_start_months: monthly ? [] : (form.period_start_months || []).map(Number),
    collect_mode: monthly ? null : apiValue(form.collect_mode),
    payment_due_days: apiInt(form.payment_due_days),
    settle_leavers: Boolean(form.settle_leavers),
    special_month: form.special_month_on ? apiInt(form.special_month) : null,
    yearly_limit: apiValue(form.yearly_limit),
    rounding: form.rounding || "NEAREST",
    slabs: toApiSlabs(form.slabs, form.special_month_on),
    exemption_reasons: toApiExemptions(form.exemptions),
    components: form.components.map((component) => {
      kindIndex[component.kind] = (kindIndex[component.kind] || 0) + 1;
      return {
        kind: component.kind,
        code: component.code,
        name: component.name,
        description: component.description || "",
        formula: component.formula,
        used_for: component.used_for || "",
        status: upper(component.status),
        sequence: kindIndex[component.kind],
      };
    }),
  };
};

// Server field paths -> the screen's field keys (slabs.2.amount -> slabs,
// exemption_reasons.0.age_from -> exemptions, calculation_components.0.formula
// -> components).
const FIELD_ALIASES = { exemption_reasons: "exemptions", calculation_components: "components", contribution_components: "components" };
const toFormErrors = (fieldErrors) => {
  if (!fieldErrors) return undefined;
  const out = {};
  Object.entries(fieldErrors).forEach(([key, message]) => {
    const head = key.replace(/^version\./, "").split(".")[0];
    const field = FIELD_ALIASES[head] || head;
    out[field] = out[field] ? `${out[field]} ${message}` : message;
  });
  return out;
};

const withFormErrors = async (promise) => {
  try {
    return await promise;
  } catch (err) {
    if (err instanceof PtApiError) err.fieldErrors = toFormErrors(err.fieldErrors);
    throw err;
  }
};

const warningsOf = (data) => (data?.warnings || []).map((item) => (typeof item === "string" ? item : text(item?.message))).filter(Boolean);
const saved = (data) => ({ version: fromApiPtVersion(data.data), message: data.message, warnings: warningsOf(data) });
const asDraft = (data) => ({ version: { ...fromApiPtVersion(data.data), status: "Draft" }, message: data.message });

// ---- Sites list (PT1) ----

const SITE_STATUS_TONES = { ACTIVE: "green", ACTIVE_NEW_VERSION: "green", NOT_SET_UP: "red" };

// "Active + Version 2 from 01 Jan 2027" shows as two chips.
const fromApiSiteRow = (row) => {
  const site = row.site || {};
  const status = upper(row.status);
  const setUp = upper(row.action) !== "SET_UP" && status !== "NOT_SET_UP";
  const [label, ...more] = (text(row.status_label) || (setUp ? "Active" : "Not set up")).split(/\s*\+\s*/);
  return {
    id: site.id,
    site: text(site.name),
    code: text(site.code),
    is_active: site.is_active !== false,
    state: text(row.state),
    local_body: text(row.local_body),
    registration_no: text(row.registration_no),
    frequency: text(row.frequency),
    frequency_label: text(row.deducted_label),
    version_label: text(row.current_version?.name),
    effective_from: text(row.effective_from),
    status,
    status_label: label,
    upcoming_label: more.join(" + "),
    status_tone: SITE_STATUS_TONES[status] || (setUp ? "green" : "red"),
    set_up: setUp,
    upcoming: status === "ACTIVE_NEW_VERSION",
    action: setUp ? "View" : "Set up",
  };
};

export const fetchPtSites = async ({ domain, companyId, signal }) => {
  const data = await ptRequest({ domain, path: "config/sites/", query: { company_id: companyId }, signal });
  if (!data || !Array.isArray(data.sites)) throw new PtApiError("The server returned an invalid PT sites list.");
  const rows = data.sites.filter((row) => row && typeof row === "object").map(fromApiSiteRow);
  const cards = data.cards || {};
  return {
    rows,
    states: (data.states || []).map(text),
    cards: {
      sites: cards.sites ?? rows.length,
      set_up: cards.set_up ?? rows.filter((row) => row.set_up).length,
      not_set_up: cards.not_set_up ?? rows.filter((row) => !row.set_up).length,
      new_version_coming: cards.new_version_coming ?? rows.filter((row) => row.upcoming).length,
    },
  };
};

// ---- Options ----

const optionList = (list) =>
  Array.isArray(list)
    ? list
        .map((item) =>
          item && typeof item === "object"
            ? { value: Array.isArray(item.value) ? months(item.value).join(",") : text(item.value ?? item.id ?? item.code), label: text(item.label ?? item.name ?? item.value), help: text(item.help) }
            : { value: text(item), label: text(item) },
        )
        .filter((option) => option.value && !option.value.startsWith("…"))
    : null;

// Every drop-down of the PT screens (load once). Half-year pairs come as
// "4,10" so they match the form's period_start_months joined.
export const fetchPtOptions = async ({ domain, companyId, signal }) => {
  const data = (await ptRequest({ domain, path: "config/options/", query: { company_id: companyId }, signal })) || {};
  return {
    frequencies: optionList(data.frequencies),
    months: optionList(data.months),
    half_year_pairs: optionList(data.half_year_pairs),
    yearly_start_months: optionList(data.yearly_start_months),
    collect_modes: optionList(data.collect_modes),
    applies_to: optionList(data.applies_to),
    exemption_modes: optionList(data.exemption_modes),
    rounding: optionList(data.rounding),
    attachments_enabled: Boolean(data.attachments_enabled),
  };
};

// ---- One site (PT1a) ----

const versionsOfSite = (detail) => {
  const list = Array.isArray(detail.versions) ? detail.versions : [detail.active_version, detail.scheduled_version];
  const seen = new Set();
  return list.filter((version) => {
    if (!version?.id || upper(version.status) === "DRAFT" || upper(version.display_status) === "DRAFT" || seen.has(version.id)) return false;
    seen.add(version.id);
    return true;
  });
};

// The site, its saved versions (full, oldest first) and its draft, if any.
// The draft is only asked for when the site says it has one.
export const fetchPtSite = async ({ domain, companyId, siteId, signal }) => {
  const query = { company_id: companyId };
  const detail = await ptRequest({ domain, path: `config/sites/${siteId}/`, query, signal });
  if (!detail || typeof detail !== "object") throw new PtApiError("The server returned an invalid PT site.");
  const fullOf = { [detail.active_version?.id]: detail.active_version, [detail.scheduled_version?.id]: detail.scheduled_version };
  const [draft, ...full] = await Promise.all([
    detail.has_draft === false ? null : ptRequest({ domain, path: `config/sites/${siteId}/draft/`, query, signal }).catch(notFoundToNull),
    // The version list carries summaries only: load each one in full.
    ...versionsOfSite(detail).map((version) => {
      const known = fullOf[version.id];
      if (Array.isArray(version.slabs)) return version;
      if (known && Array.isArray(known.slabs)) return known;
      return ptRequest({ domain, path: `config/versions/${version.id}/`, query, signal });
    }),
  ]);
  const versions = full.map(fromApiPtVersion).sort((a, b) => a.effective_from.localeCompare(b.effective_from));
  const site = detail.site || {};
  return {
    site: { id: site.id ?? siteId, name: text(site.name), code: text(site.code), state: text(site.state), local_body: text(site.local_body) },
    versions: draft ? [...versions, { ...fromApiPtVersion(draft), status: "Draft" }] : versions,
  };
};

// Set up: the blank Version 1 from the server (not saved; every statutory
// value empty).
export const fetchPtDefaults = async ({ domain, companyId, siteId, signal }) => ({
  ...fromApiPtVersion(await ptRequest({ domain, path: `config/sites/${siteId}/defaults/`, query: { company_id: companyId }, signal })),
  id: "new",
  status: "Draft",
});

// Auto-save while a draft is edited: only formats are checked.
export const savePtDraft = ({ domain, companyId, siteId, form, signal }) =>
  ptRequest({ domain, path: `config/sites/${siteId}/draft/`, method: "PUT", body: toApiPtVersion(form, companyId), signal });

export const discardPtDraft = async ({ domain, companyId, siteId }) => {
  try {
    await ptRequest({ domain, path: `config/sites/${siteId}/draft/`, method: "DELETE", body: { company_id: Number(companyId) } });
  } catch (err) {
    if (err.status !== 404) throw err;
  }
};

// Create New Version: the version copied into the site's draft from the
// first open payroll month.
export const createPtDraftFrom = async ({ domain, companyId, siteId, versionId }) =>
  asDraft(await ptRequest({ domain, path: `config/sites/${siteId}/draft/from/${versionId}/`, method: "POST", body: { company_id: Number(companyId) } }));

// Copy from site: another site's saved version into this site's draft (the
// registration number is left empty; state and local body stay this site's).
export const copyPtDraftFromSite = async ({ domain, companyId, siteId, sourceSiteId }) =>
  asDraft(
    await ptRequest({
      domain,
      path: `config/sites/${siteId}/draft/copy-from/`,
      method: "POST",
      body: { company_id: Number(companyId), source_site_id: Number(sourceSiteId) },
    }),
  );

// Save: the form becomes a saved version from its month. Returns the
// server's message and warnings (SLABS_EXCEED_LIMIT, MID_PERIOD_START).
export const createPtVersion = ({ domain, companyId, siteId, form }) =>
  withFormErrors(ptRequest({ domain, path: `config/sites/${siteId}/versions/`, method: "POST", body: toApiPtVersion(form, companyId) }).then(saved));

// Save changes to a saved version no payroll has used yet.
export const updatePtVersion = ({ domain, companyId, form }) =>
  withFormErrors(
    ptRequest({
      domain,
      path: `config/versions/${form.id}/`,
      method: "PUT",
      body: { ...toApiPtVersion(form, companyId), row_version: form.row_version },
    }).then(saved),
  );

// ---- Edit Slab drawer (PT1b) ----

// { ok, message, errors, groups } — gaps and overlaps over every slab.
export const checkPtSlabs = ({ domain, companyId, slabs, specialMonth, signal }) =>
  ptRequest({
    domain,
    path: "config/slabs/check/",
    method: "POST",
    body: {
      company_id: Number(companyId),
      slabs: toApiSlabs(slabs, Boolean(specialMonth)),
      ...(specialMonth ? { special_month: apiInt(specialMonth) } : {}),
    },
    signal,
  });

// Import slabs: reads the file on the server and saves nothing.
// { slabs (form shape), errors: ["Row 2: …"] }
export const importPtSlabs = async ({ domain, companyId, file }) => {
  const body = new FormData();
  body.append("company_id", String(companyId));
  body.append("file", file);
  const data = (await ptRequest({ domain, path: "config/slabs/import/", method: "POST", body })) || {};
  const rowErrors = (data.errors || []).map((item) => (typeof item === "string" ? { row: null, message: item } : { row: item.row ?? null, message: text(item.message) }));
  return {
    slabs: (data.slabs || []).filter((slab) => slab && typeof slab === "object").map(fromApiSlab),
    // { row, message }; row counts like a sheet (first data row = 2).
    rowErrors,
    errors: rowErrors.map((item) => `${item.row ? `Row ${item.row}: ` : ""}${item.message}`),
  };
};

// The month to test in: the version's first month, or this month if that
// has passed.
export const testMonthOf = (form) => {
  const now = moment().startOf("month");
  const start = form?.effective_from ? moment(form.effective_from).startOf("month") : null;
  return (start && start.isAfter(now) ? start : now).format("YYYY-MM");
};

// What the (unsaved) rule gives one employee in a month; saves nothing.
// Normalised for the drawers: note, sentence, steps, warnings, wage.
export const testPtEmployee = async ({ domain, companyId, siteId, employeeId, month, form, signal }) => {
  const version = toApiPtVersion(form, companyId);
  delete version.company_id;
  const data =
    (await ptRequest({
      domain,
      path: "config/test-employee/",
      method: "POST",
      body: { company_id: Number(companyId), site_id: Number(siteId), employee_id: apiId(employeeId), month, version },
      signal,
    })) || {};
  const row = (data.rows || [])[0] || {};
  return {
    note: data.estimate ? text(data.note) : "",
    sentence: text(data.sentence),
    steps: [...(data.trace?.this_month_steps || []), ...(data.trace?.settlement_note ? [data.trace.settlement_note] : [])].map(text),
    warnings: warningsOf(data),
    wage: row.monthly_wage ?? data.trace?.monthly_wage ?? null,
    pt_this_month: data.pt_this_month ?? null,
  };
};

// Employees for "Test on employee", from the PT employees list so the ids
// are the ones test-employee/ expects. This site's employees first; the
// whole company when the site has none yet.
const TEST_EMPLOYEE_PAGE = 200;
const testEmployeeRow = (item) => ({
  id: String(item.employee_id ?? item.id ?? ""),
  name: text(item.name),
  code: text(item.employee_code),
  site: text(item.site?.name),
});
export const fetchPtTestEmployees = async ({ domain, companyId, siteId, signal }) => {
  const load = async (site) => {
    const data = await ptRequest({
      domain,
      path: "employees/",
      query: { company_id: companyId, site_id: site, page: 1, page_size: TEST_EMPLOYEE_PAGE },
      signal,
    });
    const results = Array.isArray(data) ? data : data?.results || [];
    return results.filter((item) => item && typeof item === "object").map(testEmployeeRow).filter((item) => item.id && item.name);
  };
  const atSite = siteId ? await load(siteId) : [];
  return atSite.length ? atSite : load(undefined);
};

// ---- Formula (calculation components) ----

// { valid, message, codes } — "Formula is valid" or the error.
export const checkPtFormulaOnServer = ({ domain, companyId, code, formula, signal }) =>
  ptRequest({
    domain,
    path: "config/formula/check/",
    method: "POST",
    body: { company_id: Number(companyId), code, formula },
    signal,
  });

// ---- Version History (PT1c) ----

const fromApiChange = (change) => ({ field: text(change.field), label: text(change.label || change.field), before: change.before ?? null, after: change.after ?? null });
const changeList = (value) => (Array.isArray(value) ? value.filter((item) => item && typeof item === "object").map(fromApiChange) : []);

// GET config/versions/<id>/history/: the site's saved versions (newest
// first) and what changed in the asked-for version since the one before it.
export const fetchPtVersionHistory = async ({ domain, companyId, versionId, signal }) => {
  const data = await ptRequest({ domain, path: `config/versions/${versionId}/history/`, query: { company_id: companyId }, signal });
  if (!data?.selected) throw new PtApiError("The server returned an invalid version history.");
  const selected = data.selected;
  return {
    site_name: text(data.site?.name),
    versions: (Array.isArray(data.versions) ? data.versions : [])
      .filter((item) => item && typeof item === "object")
      .map((item) => ({
        id: item.id,
        name: text(item.name),
        effective_from: text(item.effective_from),
        effective_to: text(item.effective_to),
        status: text(item.display_status) || "Active",
      })),
    selected: {
      id: selected.id,
      name: text(selected.name),
      compared_with: selected.compared_with ? { id: selected.compared_with.id, name: text(selected.compared_with.name) } : null,
      changes: changeList(selected.changes),
      saved_by: text(selected.saved_by),
      saved_at: text(selected.saved_at),
      used_by: (Array.isArray(selected.used_by) ? selected.used_by : []).map((item) => (typeof item === "object" ? nameOf(item) : text(item))).filter(Boolean),
    },
  };
};

// GET config/versions/compare/?a=&b=: changes read from a (before) to b.
export const comparePtVersions = async ({ domain, companyId, a, b, signal }) => {
  const data = await ptRequest({ domain, path: "config/versions/compare/", query: { company_id: companyId, a, b }, signal });
  return { same_site: data?.same_site !== false, unchanged: Boolean(data?.unchanged), changes: changeList(data?.changes) };
};
